import { useCallback } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { decodeHtmlEntities } from '@utils/common';
import { canValidateOpgavekompleksetTask, hasOpgavekompleksetTaskInputs, radioButtonMediaInput } from '@utils/component-framework';
import { CSSRefTransition } from '@utils/react';
import { useGoToNextTaskAction } from '../../../state/actions/task.go-to-next.js';
import { useOpgavekompleksetComponent } from '../../../state/context.data.js';
import { useCurrentTask } from '../../../state/hook.current-task.js';
import { useCurrentTaskEntryId, useCurrentTaskEntry } from '../../../state/hook.entry.js';
import { useFinishEntryMutation } from '../../../state/mutations/finish.js';
import { useGoToTaskMutation } from '../../../state/mutations/task.go-to.js';
import { useAttemptMutation } from '../../../state/mutations/use-attempt.js';
import { makeSelectTaskState } from '../../../state/selectors/task.get-state.js';
import { getTaskEntryState } from '../../../utils/get-task-entry-state.js';
import { crossfadeWrapper, crossfadeContainer, crossfadeDurationMs, actions } from './_actions.style.js';
import { NextButton } from './button.next.js';
import { jsx } from 'react/jsx-runtime';

function ConditionalProgressionActions() {
  const component = useOpgavekompleksetComponent();
  const goToNextTask = useGoToNextTaskAction({
    skipFinishModal: true
  });
  const goToTaskMutation = useGoToTaskMutation();
  const finishMutation = useFinishEntryMutation();
  const makeAttempt = useAttemptMutation();
  const activeTaskId = useCurrentTaskEntryId();
  const taskState = useCurrentTaskEntry({
    select: makeSelectTaskState(component.data)
  });
  const entryState = useCurrentTaskEntry({
    select: state => getTaskEntryState(state?.stats)
  });
  const currentTask = useCurrentTask();
  const canValidate = canValidateOpgavekompleksetTask(currentTask.task);
  const hasInputs = hasOpgavekompleksetTaskInputs(currentTask.task);
  const selectedRadioButtonInput = useCurrentTaskEntry({
    select: entry => Object.entries(entry?.details ?? {}).map(([inputId, entry]) => {
      const radioButtonEntry = radioButtonMediaInput.entry.asTypeSafe(entry);
      if (!radioButtonEntry || !radioButtonEntry.markedAsCorrectAnswer) {
        return;
      }
      const inputs = currentTask.task?.type === "stack" ? currentTask.task.tasks.flatMap(it => it.content) : currentTask.task?.content;
      return inputs?.find(it => it.id === inputId);
    }).filter(it => !!it)[0]
  });
  const canProgress = useCurrentTaskEntry({
    select: entry => Object.keys(entry?.details ?? {}).length > 0
  }) ?? false;
  const handleGoToNext = useCallback(() => {
    const conditionsForCurrentTask = currentTask.task?.type === "task" ? currentTask.task.conditions : undefined;
    makeAttempt(activeTaskId);
    if (!conditionsForCurrentTask) {
      goToNextTask();
      return;
    }
    const goTo = (() => {
      const otherwise = (() => {
        switch (entryState) {
          case "correct":
            return conditionsForCurrentTask.otherwiseGoTo.onCorrect;
          case "partial":
            return conditionsForCurrentTask.otherwiseGoTo.onPartial;
          case "incorrect":
          default:
            return conditionsForCurrentTask.otherwiseGoTo.onIncorrect;
        }
      })();
      if (conditionsForCurrentTask.conditions.length && selectedRadioButtonInput) {
        return matchSelectedValueWithConditions(conditionsForCurrentTask.conditions, selectedRadioButtonInput) || otherwise;
      } else {
        return otherwise;
      }
    })();
    switch (goTo) {
      case "next":
        goToNextTask();
        return;
      case "report":
        finishMutation();
        return;
      default:
        if (goTo) {
          // if we get here, then we have the id of a task that we should
          // navigate to.
          goToTaskMutation(goTo);
        } else {
          goToNextTask();
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  currentTask.task?.type === "task" && currentTask.task.conditions, currentTask.task?.type, entryState, finishMutation, goToNextTask, goToTaskMutation, makeAttempt, selectedRadioButtonInput]);
  return /*#__PURE__*/jsx(TransitionGroup, {
    className: crossfadeWrapper,
    children: /*#__PURE__*/jsx(CSSRefTransition, {
      timeout: crossfadeDurationMs,
      classNames: crossfadeContainer,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: crossfadeContainer,
        children: /*#__PURE__*/jsx("div", {
          className: actions,
          children: /*#__PURE__*/jsx(NextButton, {
            theme: "primary",
            label: "N\xE6ste",
            onClick: handleGoToNext,
            disabled: hasInputs && !canProgress,
            tooltip: hasInputs && !canProgress ? {
              body: "Udfyld opgaven, for at gå videre",
              ariaType: "description",
              placement: "top"
            } : undefined
          })
        })
      })
    }, `${taskState === "input"}${canValidate}${hasInputs}`)
  });
}
function matchSelectedValueWithConditions(conditions, selectedAnswer) {
  const conditionsForSelectedAnswer = (() => {
    switch (selectedAnswer.type) {
      case "Text":
        return conditions.filter(it => it.answer?.startsWith("text:")).map(it => ({
          ...it,
          answer: it.answer?.replace(/^text:/, "")
        }));
      case "Image":
        return conditions.filter(it => it.answer?.startsWith("image:")).map(it => ({
          ...it,
          answer: it.answer?.replace(/^image:/, "")
        }));
      case "InlineAudio":
        return conditions.filter(it => it.answer?.startsWith("inlineaudio:")).map(it => ({
          ...it,
          answer: it.answer?.replace(/^inlineaudio:/, "")
        }));
      case "Video":
        return conditions.filter(it => it.answer?.startsWith("video:")).map(it => ({
          ...it,
          answer: it.answer?.replace(/^video:/, "")
        }));
    }
  })();
  switch (selectedAnswer.type) {
    case "Text":
      return conditionsForSelectedAnswer?.find(it => decodeHtmlEntities(it.answer ?? "") === selectedAnswer.text)?.goTo ?? null;
    case "Image":
      {
        const selectedAnswerPathname = getPathnameFromSrc(selectedAnswer.src);
        return conditionsForSelectedAnswer?.find(it => getPathnameFromSrc(it.answer) === selectedAnswerPathname)?.goTo ?? null;
      }
    case "InlineAudio":
      {
        const selectedAnswerPathname = getPathnameFromSrc(selectedAnswer.src);
        return conditionsForSelectedAnswer?.find(it => getPathnameFromSrc(it.answer) === selectedAnswerPathname)?.goTo ?? null;
      }
    case "Video":
      return conditionsForSelectedAnswer?.find(it => it.answer === selectedAnswer.twentyThreeVideo.id)?.goTo ?? null;
    default:
      return null;
  }
}
function getPathnameFromSrc(src) {
  if (!src) {
    return undefined;
  }
  try {
    return new URL(src, "https://localhost").pathname;
  } catch {
    return src;
  }
}

export { ConditionalProgressionActions };
//# sourceMappingURL=integration.conditional-progression.js.map
